import { Languages, MessageCircle, Send, User } from "lucide-react-native";
import { ComponentRef, FC, useRef, useState } from "react";
import { ScrollView } from "react-native";

import {
  Box,
  Button,
  ButtonIcon,
  Card,
  Center,
  ErrorWithRetry,
  HStack,
  Input,
  InputField,
  Media,
  MediaGallery,
  Pressable,
  Skeleton,
  Text,
  TypingIndicator,
  VStack,
} from "@/components/ui";
import { useLocalization } from "@/hooks";
import API from "@/hooks/api";
import { ConversationMessage, Persona } from "@/types/resources";
import { ResourceType } from "@/types/utils";

/* ── Types ──────────────────────────────────────────────── */

interface PersonaChatProps {
  conversation: ConversationMessage[];
  disabled?: boolean;
  error: string | null;
  loading?: boolean;
  onRetry: () => void;
  persona: Persona | null;
  personaId: string;
  resourceId: string;
  resourceType: ResourceType;
  runId: string;
  translatedConversation: ConversationMessage[];
}

/* ── Constants ──────────────────────────────────────────── */

const PURPLE = "#7c3aed";

/* ── Skeleton ───────────────────────────────────────────── */

export const PersonaChatSkeleton: FC = () => (
  <Card className="flex-1 p-0 overflow-hidden border border-outline-200 rounded-2xl">
    <HStack className="px-6 py-4 border-b border-outline-200 items-center gap-3 shrink-0">
      <Box className="w-9 h-9 rounded-full items-center justify-center shrink-0 bg-violet-100">
        <User color={PURPLE} size={16} />
      </Box>
      <VStack className="gap-1">
        <Skeleton className="h-4 w-32 rounded mb-1" />
        <Skeleton className="h-3 w-24 rounded" />
      </VStack>
    </HStack>
    <Box className="flex-1 bg-background-50 dark:bg-background-950 min-h-0">
      <ScrollView className="flex-1 px-6 py-4">
        <VStack className="gap-4">
          {[1, 2, 3, 4].map((i) => (
            <HStack
              className={i % 2 === 0 ? "justify-end" : "justify-start"}
              key={i}
            >
              <Skeleton
                className={`h-16 rounded-2xl ${i % 2 === 0 ? "w-[65%] rounded-tr-sm" : "w-[70%] rounded-tl-sm"}`}
              />
            </HStack>
          ))}
        </VStack>
      </ScrollView>
    </Box>
    <HStack className="px-4 py-3 border-t border-outline-200 bg-white dark:bg-background-900 gap-2 items-end shrink-0">
      <Skeleton className="flex-1 h-10 rounded-xl" />
      <Skeleton className="w-10 h-10 rounded-lg shrink-0" />
    </HStack>
  </Card>
);

/* ── Component ──────────────────────────────────────────── */

const PersonaChat: FC<PersonaChatProps> = ({
  conversation: initialConversation,
  disabled = false,
  error,
  loading,
  onRetry,
  persona,
  personaId,
  resourceId,
  resourceType,
  runId,
  translatedConversation: initialTranslated,
}) => {
  const { i18n } = useLocalization();

  const studySendHook = API.Studies.useSendConversationMessage(
    resourceId,
    runId,
    personaId
  );
  const adhocSendHook = API.Adhoc.useSendConversationMessage(
    resourceId,
    runId,
    personaId
  );
  const {
    clearError,
    error: sendError,
    loading: sending,
    sendMessage,
  } = resourceType === "study" ? studySendHook : adhocSendHook;

  const [conversation, setConversation] =
    useState<ConversationMessage[]>(initialConversation);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [galleryMedia, setGalleryMedia] = useState<string[]>([]);
  const [galleryVisible, setGalleryVisible] = useState(false);
  const [inputHeight, setInputHeight] = useState(40);
  const [inputMessage, setInputMessage] = useState("");
  const [showTranslated, setShowTranslated] = useState(
    () => initialTranslated.length > 0
  );
  const [translatedConversation, setTranslatedConversation] =
    useState<ConversationMessage[]>(initialTranslated);
  const chatScrollRef = useRef<ComponentRef<typeof ScrollView>>(null);

  // Sync the conversation from props during render (not in an effect) when the
  // initial data changes; the values above seed the initial mount.
  const [prevInitial, setPrevInitial] = useState({
    conversation: initialConversation,
    translated: initialTranslated,
  });
  if (
    prevInitial.conversation !== initialConversation ||
    prevInitial.translated !== initialTranslated
  ) {
    setPrevInitial({
      conversation: initialConversation,
      translated: initialTranslated,
    });
    setConversation(initialConversation);
    setTranslatedConversation(initialTranslated);
    if (initialTranslated.length > 0) setShowTranslated(true);
  }

  const hasTranslation = translatedConversation.length > 0;
  const messages = showTranslated ? translatedConversation : conversation;

  const handleSend = async () => {
    if (!inputMessage.trim() || sending) return;
    if (sendError) clearError();

    const text = inputMessage;
    const userMsg: ConversationMessage = { content: [{ text }], role: "user" };
    setConversation((p) => [...p, userMsg]);
    setTranslatedConversation((p) => [...p, userMsg]);
    setInputMessage("");
    setInputHeight(40);

    const result = await sendMessage({ message: text });
    if (result) {
      setConversation(result.conversation);
      setTranslatedConversation(result.translated_conversation);
    } else {
      setConversation((p) => p.slice(0, -1));
      setTranslatedConversation((p) => p.slice(0, -1));
      setInputMessage(text);
    }
  };

  const openGallery = (media: string[], index: number) => {
    setGalleryMedia(media);
    setGalleryIndex(index);
    setGalleryVisible(true);
  };

  const renderMessage = (msg: ConversationMessage, idx: number) => {
    const isUser = msg.role === "user";
    const msgMedia = msg.content
      .filter((c) => "image" in c || "video" in c)
      .map((c) =>
        "image" in c
          ? c.image.source
          : (c as { video: { source: string } }).video.source
      );

    return (
      <VStack
        key={idx}
        className={`gap-1 ${isUser ? "items-end" : "items-start"}`}
      >
        <HStack className={isUser ? "justify-end" : "justify-start"}>
          {!isUser && (
            <Box className="w-7 h-7 rounded-full items-center justify-center mr-2 mt-1 shrink-0 bg-violet-100">
              <User color={PURPLE} size={13} />
            </Box>
          )}
          <Card
            className={`max-w-[75%] p-4 rounded-2xl border ${
              isUser
                ? "min-w-[200px] bg-purple-50 border-purple-200 rounded-tr-sm"
                : "bg-white border-outline-200 rounded-tl-sm"
            }`}
          >
            <VStack className="gap-2">
              {msg.content.map((c, i) =>
                "text" in c ? (
                  <Text
                    className={`text-sm leading-relaxed ${isUser ? "text-purple-900" : "text-typography-700"}`}
                    key={i}
                  >
                    {c.text}
                  </Text>
                ) : null
              )}
              {msgMedia.length > 0 && (
                <HStack className="gap-2 flex-wrap">
                  {msgMedia.map((src, i) => (
                    <Media
                      key={i}
                      onPress={() => openGallery(msgMedia, i)}
                      source={src}
                    />
                  ))}
                </HStack>
              )}
            </VStack>
          </Card>
        </HStack>
        {showTranslated && persona?.general.language && (
          <Text
            className={`text-[10px] px-1 text-violet-400 ${!isUser ? "ml-9" : ""}`}
          >
            {i18n.t("misc.translatedFrom", {
              language: persona.general.language,
            })}
          </Text>
        )}
      </VStack>
    );
  };

  return (
    <>
      <MediaGallery
        initialIndex={galleryIndex}
        media={galleryMedia}
        onClose={() => setGalleryVisible(false)}
        visible={galleryVisible}
      />
      <Card className="flex-1 p-0 overflow-hidden border border-outline-200 rounded-2xl">
        {/* Header */}
        <HStack className="px-6 py-4 border-b border-outline-200 items-center justify-between shrink-0">
          <HStack className="items-center gap-3">
            <Box className="w-9 h-9 rounded-full items-center justify-center shrink-0 bg-violet-100">
              <User color={PURPLE} size={16} />
            </Box>
            <VStack className="gap-0">
              {loading ? (
                <>
                  <Skeleton className="h-4 w-32 rounded mb-1" />
                  <Skeleton className="h-3 w-24 rounded" />
                </>
              ) : persona ? (
                <>
                  <HStack className="items-center gap-2">
                    <MessageCircle color={PURPLE} size={14} />
                    <Text className="text-sm font-semibold text-typography-900">
                      {i18n.t("dashboardNavigator.conversation")}
                    </Text>
                  </HStack>
                  <Text className="text-xs text-typography-400">
                    {[
                      i18n.t("dashboardNavigator.age", {
                        age: persona.general.age,
                      }),
                      persona.general.gender,
                      persona.residency.city,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </Text>
                </>
              ) : null}
            </VStack>
          </HStack>

          {hasTranslation && (
            <Pressable
              className={`flex-row items-center gap-1.5 px-2.5 py-1.5 rounded-lg border ${
                !showTranslated
                  ? "bg-violet-50 border-violet-300"
                  : "bg-background-50 border-outline-200"
              }`}
              onPress={() => setShowTranslated((v) => !v)}
            >
              <Languages
                color={!showTranslated ? PURPLE : undefined}
                size={13}
                className={showTranslated ? "text-typography-500" : undefined}
              />
              <Text
                className={`text-xs font-medium ${!showTranslated ? "text-violet-600" : "text-typography-500"}`}
              >
                {showTranslated
                  ? i18n.t("misc.showOriginal")
                  : i18n.t("misc.showTranslation")}
              </Text>
            </Pressable>
          )}
        </HStack>

        {/* Messages */}
        <Box className="flex-1 bg-background-50 dark:bg-background-950 min-h-0">
          {loading ? (
            <ScrollView className="flex-1 px-6 py-4">
              <VStack className="gap-4">
                {[1, 2, 3, 4].map((i) => (
                  <HStack
                    className={i % 2 === 0 ? "justify-end" : "justify-start"}
                    key={i}
                  >
                    <Skeleton
                      className={`h-16 rounded-2xl ${i % 2 === 0 ? "w-[65%] rounded-tr-sm" : "w-[70%] rounded-tl-sm"}`}
                    />
                  </HStack>
                ))}
              </VStack>
            </ScrollView>
          ) : error ? (
            <Center className="flex-1 px-6">
              <ErrorWithRetry
                error={error}
                onRetry={onRetry}
                title={i18n.t("dashboardNavigator.errorLoadingConversation")}
              />
            </Center>
          ) : (
            <ScrollView
              className="flex-1 px-6 py-4"
              onContentSizeChange={() =>
                chatScrollRef.current?.scrollToEnd({ animated: false })
              }
              ref={chatScrollRef}
              showsVerticalScrollIndicator={false}
            >
              <VStack className="gap-4">
                {messages.map((msg, idx) => renderMessage(msg, idx))}
                <TypingIndicator isTyping={sending} />
                {sendError && (
                  <Text className="text-sm text-error-500 text-center">
                    {sendError}
                  </Text>
                )}
              </VStack>
            </ScrollView>
          )}
        </Box>

        {/* Input */}
        <HStack className="px-4 py-3 border-t border-outline-200 bg-white dark:bg-background-900 gap-2 items-end shrink-0">
          <Input
            className="flex-1 h-auto border border-outline-200 rounded-xl bg-background-50"
            isDisabled={disabled || sending || loading}
            style={{ minHeight: 40, maxHeight: 120, height: inputHeight }}
          >
            <InputField
              className="text-typography-700 dark:text-typography-300 py-2"
              maxLength={5000}
              multiline
              onChangeText={(text) => {
                setInputMessage(text);
                if (sendError) clearError();
              }}
              onContentSizeChange={(e) =>
                setInputHeight(
                  Math.min(Math.max(e.nativeEvent.contentSize.height, 40), 120)
                )
              }
              onKeyPress={(e) => {
                if (e.nativeEvent.key === "Enter" && !e.nativeEvent.shiftKey) {
                  e.preventDefault();
                  if (inputMessage.trim() && !sending && !loading) handleSend();
                }
              }}
              placeholder={i18n.t("misc.typeMessage")}
              value={inputMessage}
            />
          </Input>
          <Button
            className="bg-primary-500"
            isDisabled={disabled || !inputMessage.trim() || sending || loading}
            onPress={handleSend}
            size="md"
          >
            <ButtonIcon as={Send} />
          </Button>
        </HStack>
      </Card>
    </>
  );
};

/* ── Export ─────────────────────────────────────────────── */

export default PersonaChat;
